import { Autocomplete, AutocompleteProps, Box, TextField, TextFieldProps } from '@mui/material';
import { find, get, map } from 'lodash';
import { useMemo } from 'react';
import FlagCountry from 'src/components/common/FlagCountry';
import { IJurisdictionOption } from 'src/components/Form/FormItemJurisdiction';
import { useQueryJurisdictions } from 'src/services/queries';
import {
  checkCountryHaveState,
  formatJurisdictionName,
  getCountryByCountryCode,
} from 'src/utils/helpers/stringHelpers';

interface IProps extends Omit<AutocompleteProps<IJurisdictionOption, false, false, false>, 'renderInput' | 'options'> {
  inputProps?: TextFieldProps;
  selected?: string;
  keySelected: keyof IJurisdictionOption;
}

const FormItemJurisdictionSingle = (props: IProps) => {
  const { data, isFetching } = useQueryJurisdictions();

  const options: IJurisdictionOption[] = useMemo(() => {
    const mappingWithCountryState: IJurisdictionOption[] = map<IJurisdictions, IJurisdictionOption>(data.data, (item) => {
      const isCountryHaveState = checkCountryHaveState(item?.country_code);
      if (isCountryHaveState) {
        const country = getCountryByCountryCode(item.country_code);
        return {
          ...item,
          ...{
            optionLabel: `${country.label} - ${formatJurisdictionName(item?.jurisdiction || '', item?.country_code || '')}`,
          },
        } as IJurisdictionOption;
      }
      return {
        ...item,
        ...{
          optionLabel: item.jurisdiction,
        },
      };
    });
    return mappingWithCountryState.sort((a, b) => a.optionLabel.localeCompare(b.optionLabel));
  }, [data.data]);

  const selectedOption = useMemo(() => {
    const option = find(options, (item) => get(item, props.keySelected) === props.selected);
    return (option as IJurisdictionOption) ?? null;
  }, [props.selected, options]);

  return (
    <Autocomplete
      value={selectedOption}
      isOptionEqualToValue={(option, value) => option.country_code === value.country_code}
      loading={isFetching}
      renderOption={(props, option, state, ownerState) => {
        const { key, ...optionProps } = props;
        return (
          <Box display={'flex'} gap={1} key={key} component="li" {...optionProps}>
            <FlagCountry code={option.country_code} /> {ownerState.getOptionLabel(option)}
          </Box>
        );
      }}
      getOptionLabel={(option) => option.optionLabel}
      getOptionKey={(option) => option.optionLabel}
      renderInput={(params) => {
        return (
          <TextField
            {...props.inputProps}
            {...params}
            InputProps={{
              ...params.InputProps,
              startAdornment: selectedOption && <FlagCountry code={selectedOption.country_code} />,
            }}
            size="small"
          />
        );
      }}
      options={options}
      {...props}
    />
  );
};

export default FormItemJurisdictionSingle;
